import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import lodash from 'lodash';

import MatchEvent from 'interfaces/matchEvent';

import { Match } from 'interfaces/match';
import Team from 'interfaces/team';

import { selectActiveTeamOption } from 'features/input/inputSlice';

import handleRejected from 'helpers/handleRejected';
import backendMatchTransformer from '../match/helpers/backendMatchTransformer';

import generatePlayerOptions from './helpers/generatePlayerOptions';

import { annulMatchEvent, deleteMatchEvent, getMatchById, patchMatch, patchMatchEvent, postMatchEvent } from './thunks';

import { MatchSliceState } from './matchSliceTypes';
import { RootState } from 'rootReducer';
import returnZeroIfNotDefined from 'helpers/returnZeroIfNotDefined';

import filterAndSortMatchEvents from './helpers/filterAndSortMatchEvents';
import setRedCardedPlayerToNotOnTheField from './helpers/setRedCardedPlayerToNotOnTheField';
import setSubstitutionPlayersState from './helpers/setSubstitutionPlayersState';
import setDeleteRedCardedPlayerToOnTheField from './helpers/setDeleteRedCardedPlayerToOnTheField';
import setDeleteSubstitutionPlayersState from './helpers/setDeleteSubstitutionPlayersState';
import { TeamOption } from '../../enums/teamOption';
import { MatchEventType } from '../../enums/matchEventType';
import countEventPerTeam from '../stats/helpers/countEventPerTeam';
import MatchStatistic from '../../interfaces/matchStatistic';
import getMatchStatisticForType from '../stats/helpers/getMatchStatisticForType';
import { MatchStatisticType } from '../../enums/matchStatisticType';
import { StatsSelect } from '../../components/Stats/StatsTypes';
import getStatsCountForType from '../stats/helpers/getStatsCountForType';
import addGoalToScore from './helpers/addGoalToScore';
import removeGoalFromScore from './helpers/removeGoalFromScore';
import { storeItemToLog } from '../log/storeItemToLog';
import { v4 as uuidv4 } from 'uuid';

const initialState: MatchSliceState = {
  loading: true,
  error: false,
  showStatusDropdown: false,
  match: {
    name: '',
    matchEvents: [],
    homeTeam: {
      persons: [],
      staff: [],
      name: '',
      id: 0,
    },
    awayTeam: {
      persons: [],
      staff: [],
      name: '',
      id: 0,
    },
    stage: {
      name: '',
      stageTypeId: 0,
      id: 0,
    },
    matchStatusId: 0,
    matchTypeId: null,
    id: 0,
    sportId: 0,
    matchLength: '',
    liveFeeds: [],
    matchStatisticsAdmin: [],
  },
};

export const matchSlice = createSlice({
  name: 'matchState',
  initialState,
  reducers: {
    addEvent(state, action) {
      const { matchEvents } = state.match;
      const matchEvent = lodash.clone(action.payload);

      matchEvents.unshift(matchEvent);

      setRedCardedPlayerToNotOnTheField(state, matchEvent);
      setSubstitutionPlayersState(state, matchEvent);
      addGoalToScore(state, matchEvent);
    },
    removeEvent(state, action) {
      const { matchEvents } = state.match;
      const matchEvent = lodash.clone(action.payload);
      removeGoalFromScore(state, matchEvent);

      state.match.matchEvents = matchEvents.filter((mE) => {
        if (matchEvent.id !== undefined) {
          if (!mE.id || matchEvent.id !== mE.id) {
            return true;
          }
        } else {
          if (mE.id || matchEvent.liveFeedSerialId !== mE.liveFeedSerialId) {
            return true;
          }
        }
        return false;
      });
    },
    removeGoalFromScore(state, action) {
      const matchEvent = lodash.clone(action.payload);
      removeGoalFromScore(state, matchEvent);
    },
    updateEvent(state, action) {
      const matchEvent = lodash.clone(action.payload as MatchEvent);
      let originalPersonId;

      state.match.matchEvents = state.match.matchEvents.map((originalMatchEvent: MatchEvent) => {
        const internal = originalMatchEvent.internal;
        if (matchEvent.id !== undefined && matchEvent.id === originalMatchEvent.id) {
          originalPersonId = originalMatchEvent.person ? originalMatchEvent.person.id : undefined;
          const mergedEvent = lodash.merge(originalMatchEvent, matchEvent);
          originalMatchEvent.internal = internal;
          return mergedEvent;
        } else if (
          matchEvent.liveFeedSerialId === originalMatchEvent.liveFeedSerialId &&
          matchEvent.liveFeedSerialId !== undefined &&
          originalMatchEvent.liveFeedSerialId !== undefined
        ) {
          originalPersonId = originalMatchEvent.person ? originalMatchEvent.person.id : undefined;
          const mergedEvent = lodash.merge(originalMatchEvent, matchEvent);
          originalMatchEvent.internal = internal;
          return mergedEvent;
        }

        return originalMatchEvent;
      });

      if (originalPersonId) {
        setDeleteRedCardedPlayerToOnTheField(state, matchEvent, originalPersonId);
        setDeleteSubstitutionPlayersState(state, matchEvent, originalPersonId);
      }
      setRedCardedPlayerToNotOnTheField(state, matchEvent);
      setSubstitutionPlayersState(state, matchEvent);
    },
    addMatchEventId(state, action) {
      const { id, liveFeedSerialId } = action.payload;

      state.match.matchEvents = state.match.matchEvents.map((matchEvent: MatchEvent) => {
        if (liveFeedSerialId === matchEvent.liveFeedSerialId) {
          matchEvent.id = id;
        }
        return matchEvent;
      });
    },
    addMatchStatistic(state, action) {
      if (!state.match.matchStatisticsAdmin) {
        state.match.matchStatisticsAdmin = [];
      }
      state.match.matchStatisticsAdmin.push(action.payload);
    },
    updateMatchStatistic(state, action) {
      const { id, status, teamId, matchStatisticTypeId, statisticsCount } = action.payload;
      state.match.matchStatisticsAdmin = state.match.matchStatisticsAdmin?.map((matchStatistic: MatchStatistic) => {
        if (matchStatistic.teamId === teamId && matchStatistic.matchStatisticsTypeId === matchStatisticTypeId) {
          matchStatistic.id = id;
          matchStatistic.status = status;
          matchStatistic.statisticsCount = statisticsCount;
        }
        return matchStatistic;
      });
    },
    setEventInternal(state, action) {
      const { id, loading, error, liveFeedSerialId, mainEvent, active, status, previousValue, sorting } =
        action.payload;

      state.match.matchEvents = state.match.matchEvents.map((matchEvent: MatchEvent) => {
        if (
          (liveFeedSerialId !== undefined && liveFeedSerialId === matchEvent.liveFeedSerialId) ||
          (id !== undefined && id === matchEvent.id)
        ) {
          const internal = matchEvent?.internal ? lodash.cloneDeep(matchEvent?.internal) : {};

          if (error !== undefined) {
            if (error === false && internal?.error === undefined) {
              internal.error = undefined;
            } else {
              internal.error = error;
            }
          }

          if (loading !== undefined) {
            internal.loading = loading;
          }

          if (status !== undefined) {
            internal.status = status;
          }

          if (previousValue !== undefined) {
            internal.previousValue = previousValue;
          }

          if (mainEvent !== undefined) {
            internal.mainEvent = mainEvent;
          }

          if (active !== undefined) {
            internal.active = active;
          }

          if (sorting !== undefined) {
            internal.sorting = sorting;
          }

          return lodash.merge(matchEvent, {
            internal: internal,
          });
        }

        return matchEvent;
      });
    },
    clearActiveMatchEvents(state) {
      state.match.matchEvents = state.match.matchEvents.map((matchEvent: MatchEvent) => {
        if (matchEvent.internal) {
          matchEvent.internal.active = false;
        }
        return matchEvent;
      });
    },
    setStatusDropdown(state, action) {
      state.showStatusDropdown = action.payload;
    },
    toggleStatusDropdown(state) {
      state.showStatusDropdown = !state.showStatusDropdown;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMatchById.fulfilled, (state, action) => {
      const match = backendMatchTransformer(action.payload as Match);
      state.match = Object.assign({}, state.match, match);
      state.match.matchEvents.reverse();

      state.loading = false;
    });

    builder.addCase(annulMatchEvent.pending, (state, action) => {
      const event = action.meta.arg;
      const { id } = event;

      state.match.matchEvents = state.match.matchEvents.map((matchEvent: MatchEvent) => {
        if (matchEvent.id === id) {
          return {
            ...matchEvent,
            matchEventTypeId: MatchEventType.DISALLOWED_GOAL,
          };
        }

        return matchEvent;
      });
    });

    builder.addCase(patchMatch.pending, (state, action) => {
      const payload = action.meta.arg;
      state.match = lodash.merge(state.match, payload);
    });

    builder.addCase(deleteMatchEvent.fulfilled, (state, action) => {
      const event = action.meta.arg;

      setDeleteRedCardedPlayerToOnTheField(state, event);
      setDeleteSubstitutionPlayersState(state, event);

      state.match.matchEvents = state.match.matchEvents.filter((matchEvent: MatchEvent) => matchEvent.id !== event.id);
    });

    handleRejected(
      builder,
      [annulMatchEvent, deleteMatchEvent, postMatchEvent, patchMatchEvent, patchMatch],
      (state, action) => {
        console.log('Error occurred: ', action);
        const requestId = uuidv4();
        storeItemToLog('Match slice error (1)', action, state.match.id, requestId);
      },
    );

    handleRejected(builder, [getMatchById], (state, action) => {
      console.log('Error occurred: ', action);
      state.error = true;
      state.loading = false;
      const requestId = uuidv4();
      storeItemToLog('Match slice error (2)', action, state.match.id, requestId);
    });
  },
});

export const selectMatch = (state: RootState): Match => state.matchState.match;
export const selectLoading = (state: RootState): boolean => state.matchState.loading;
export const selectError = (state: RootState): boolean => state.matchState.error;
export const selectMatchStatusId = (state: RootState): number => {
  return state.matchState.match.matchStatusId;
};
export const selectMatchEvents = (state: RootState): MatchEvent[] => state.matchState.match.matchEvents;
export const selectYellowCardEvents = createSelector(
  (state: RootState) => state.matchState.match.matchEvents,
  (matchEvents) => {
    return matchEvents.filter((matchEvent) => matchEvent.matchEventTypeId === MatchEventType.YELLOW_CARD);
  },
);
export const selectRedCardEvents = createSelector(
  (state: RootState) => state.matchState.match.matchEvents,
  (matchEvents) => {
    return matchEvents.filter((matchEvent) => matchEvent.matchEventTypeId === MatchEventType.RED_CARD);
  },
);
export const selectShowStatusDropdown = (state: RootState): boolean => state.matchState.showStatusDropdown;
export const selectHomeTeam = (state: RootState): Team => state.matchState.match.homeTeam;
export const selectAwayTeam = (state: RootState): Team => state.matchState.match.awayTeam;
export const selectAllPlayers = createSelector(
  [
    (state: RootState) => state.matchState.match.homeTeam.persons,
    (state: RootState) => state.matchState.match.awayTeam.persons,
  ],
  (homeTeamPlayers, awayTeamPlayers) => {
    return homeTeamPlayers.concat(awayTeamPlayers);
  },
);
export const selectAllStaff = createSelector(
  [
    (state: RootState) => state.matchState.match.homeTeam.staff,
    (state: RootState) => state.matchState.match.awayTeam.staff,
  ],
  (homeTeamStaff, awayTeamStaff) => {
    return homeTeamStaff.concat(awayTeamStaff);
  },
);
export const selectFilteredAndSortedMatchEvents = (state: RootState): MatchEvent[] => {
  return filterAndSortMatchEvents(state.matchState.match.matchEvents);
};

export const selectActiveMatchEvents = createSelector(
  (state: RootState) => state.matchState.match.matchEvents,
  (matchEvents) => {
    return matchEvents.filter((matchEvent) => matchEvent.internal?.active);
  },
);

export const selectMatchLength = (state: RootState): string | undefined => state.matchState.match.matchLength;
export const selectActiveTeam = createSelector(
  [selectActiveTeamOption, selectHomeTeam, selectAwayTeam],
  (teamOption, homeTeam, awayTeam) => {
    if (teamOption === TeamOption.AWAY) return awayTeam;
    if (teamOption === TeamOption.HOME) return homeTeam;
    return null;
  },
);
export const selectActiveTeamOpposite = createSelector(
  [selectActiveTeamOption, selectHomeTeam, selectAwayTeam],
  (teamOption, homeTeam, awayTeam) => {
    if (teamOption === TeamOption.AWAY) return homeTeam;
    if (teamOption === TeamOption.HOME) return awayTeam;
    return null;
  },
);
export const selectActiveTeamPlayersOnTheField = createSelector([selectActiveTeam], (team) => {
  if (!team) return null;
  return team.persons.filter((player) => player.startsMatch);
});
export const selectActiveTeamPlayersOnTheFieldOptions = createSelector(
  [selectActiveTeamPlayersOnTheField],
  (players) => {
    if (!players) return null;

    return generatePlayerOptions(players);
  },
);
export const selectActiveTeamPlayerOptions = createSelector(selectActiveTeam, (team) => {
  if (!team) return null;

  return generatePlayerOptions(team.persons);
});

export const selectActiveTeamPlayerAndStaffOptions = createSelector(selectActiveTeam, (team) => {
  if (!team) return null;

  return generatePlayerOptions(team.persons, team.staff);
});

// Select the player options of the opposite team.
export const selectActiveTeamPlayerOptionsOpposite = createSelector(selectActiveTeamOpposite, (team) => {
  if (!team) return null;

  return generatePlayerOptions(team.persons);
});
export const selectRedCardStats = createSelector(selectMatch, (match) => {
  const stats = match.matchStatisticsAdmin || [];
  const homeStats = getMatchStatisticForType(stats, match.homeTeam.id, MatchStatisticType.RED_CARDS);
  const awayStats = getMatchStatisticForType(stats, match.awayTeam.id, MatchStatisticType.RED_CARDS);
  return {
    home: homeStats?.statisticsCount ?? 0,
    away: awayStats?.statisticsCount ?? 0,
  };
});

export const selectYellowCardStats = createSelector(selectMatch, (match) => {
  const stats = match.matchStatisticsAdmin || [];
  const homeStats = getMatchStatisticForType(stats, match.homeTeam.id, MatchStatisticType.YELLOW_CARDS);
  const awayStats = getMatchStatisticForType(stats, match.awayTeam.id, MatchStatisticType.YELLOW_CARDS);
  return {
    home: homeStats?.statisticsCount ?? 0,
    away: awayStats?.statisticsCount ?? 0,
  };
});

export const selectTotalShotsStats = createSelector(selectMatch, (match) => {
  const stats = match.matchStatisticsAdmin || [];
  const homeStats = getMatchStatisticForType(stats, match.homeTeam.id, MatchStatisticType.TOTAL_SHOTS);
  const awayStats = getMatchStatisticForType(stats, match.awayTeam.id, MatchStatisticType.TOTAL_SHOTS);
  return {
    home: homeStats?.statisticsCount ?? 0,
    away: awayStats?.statisticsCount ?? 0,
  };
});

export const selectCornerStats = createSelector(selectMatch, (match) => {
  return countEventPerTeam(MatchEventType.CORNER, match);
});
export const selectOffsideStats = createSelector(selectMatch, (match) => {
  return countEventPerTeam(MatchEventType.OFFSIDE, match);
});
export const selectFreeKickStats = createSelector(selectMatch, (match) => {
  return countEventPerTeam(MatchEventType.FREE_KICK, match);
});

export const selectMatchStatisticsAdmin = (state: RootState): MatchStatistic[] | undefined => {
  const { match } = state.matchState;

  return match.matchStatisticsAdmin;
};

const getStats = (match: Match, matchStatisticsTypeId: number) => {
  const stats = match.matchStatisticsAdmin || [];
  const teamIds = {
    homeTeamId: match.homeTeam.id,
    awayTeamId: match.awayTeam.id,
  };
  const homeStats = getMatchStatisticForType(stats, teamIds.homeTeamId, matchStatisticsTypeId);
  const awayStats = getMatchStatisticForType(stats, teamIds.awayTeamId, matchStatisticsTypeId);
  const homeCount = returnZeroIfNotDefined(homeStats?.statisticsCount);
  const awayCount = returnZeroIfNotDefined(awayStats?.statisticsCount);

  return {
    data: {
      homeCount: homeCount,
      awayCount: awayCount,
    },
    teamIds: {
      homeTeamId: teamIds.homeTeamId,
      awayTeamId: teamIds.awayTeamId,
    },
    ids: {
      homeId: homeStats?.id,
      awayId: awayStats?.id,
    },
    matchId: match.id,
  };
};

export const selectShotsOnGoal = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.SHOTS_ON_TARGET);
});

export const selectPostHits = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.POST_HITS);
});

export const selectChances = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.CHANCES);
});

export const selectSaves = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.SAVES);
});

export const selectYellowCards = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.YELLOW_CARDS);
});

export const selectRedCards = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.RED_CARDS);
});

export const selectOffsides = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.OFFSIDES);
});

export const selectCorners = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.CORNERS);
});

export const selectFreeKicks = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.FREE_KICKS);
});

export const selectTotalShots = createSelector(selectMatch, (match): StatsSelect => {
  return getStats(match, MatchStatisticType.TOTAL_SHOTS);
});

export const selectShotsOffTarget = createSelector(selectMatch, (match): StatsSelect => {
  const stats = match.matchStatisticsAdmin || [];
  const teamIds = {
    homeTeamId: match.homeTeam.id,
    awayTeamId: match.awayTeam.id,
  };
  const homeStats = getMatchStatisticForType(stats, teamIds.homeTeamId, MatchStatisticType.TOTAL_SHOTS);
  const awayStats = getMatchStatisticForType(stats, teamIds.awayTeamId, MatchStatisticType.TOTAL_SHOTS);
  const totalShotsHome = returnZeroIfNotDefined(homeStats?.statisticsCount);
  const totalShotsAway = returnZeroIfNotDefined(awayStats?.statisticsCount);
  const postHitsHome = returnZeroIfNotDefined(
    getStatsCountForType(stats, teamIds.homeTeamId, MatchStatisticType.POST_HITS),
  );
  const postHitsAway = returnZeroIfNotDefined(
    getStatsCountForType(stats, teamIds.awayTeamId, MatchStatisticType.POST_HITS),
  );
  const shotsOnGoalHome = returnZeroIfNotDefined(
    getStatsCountForType(stats, teamIds.homeTeamId, MatchStatisticType.SHOTS_ON_TARGET),
  );
  const shotsOnGoalAway = returnZeroIfNotDefined(
    getStatsCountForType(stats, teamIds.awayTeamId, MatchStatisticType.SHOTS_ON_TARGET),
  );

  const shotsSumHome = shotsOnGoalHome + postHitsHome;
  const shotsSumAway = shotsOnGoalAway + postHitsAway;

  const home = totalShotsHome - shotsSumHome;
  const away = totalShotsAway - shotsSumAway;

  return {
    data: {
      homeCount: home,
      awayCount: away,
    },
    teamIds: {
      homeTeamId: teamIds.homeTeamId,
      awayTeamId: teamIds.awayTeamId,
    },
    ids: {
      homeId: homeStats?.id,
      awayId: awayStats?.id,
    },
    matchId: match.id,
  };
});

export const matchSliceActions = matchSlice.actions;

export default matchSlice.reducer;
